import { Component, computed, inject, OnInit, signal, WritableSignal, ChangeDetectionStrategy } from "@angular/core";
import { Table, TableModule } from "@openng/optimus-ui/table";
import { Column } from "../../models/ui/tableColumn";
import { DEVICE_STATUS, DEVICE_TYPE, DeviceTableRow, DeviceType } from "../../models/api/deviceManagement/device";
import { DeviceManagementService } from "../../services/api/device-management/device-management.service";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { InputTextModule } from "@openng/optimus-ui/inputtext";
import { CheckboxModule } from "@openng/optimus-ui/checkbox";
import { FormsModule } from "@angular/forms";
import { ButtonComponent } from "../../shared/components/button/button.component";
import { DialogService, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";
import { StartUpdateComponent } from "./components/start-update.component/start-update.component";
import { SelectModule } from "@openng/optimus-ui/select";
import { TabsModule } from "@openng/optimus-ui/tabs";
import { first, map, tap } from "rxjs";
import { Software } from "../../models/api/deviceManagement/software";
import { SoftwareForm } from "./components/software-form/software-form";
import { SimplebarAngularModule } from "simplebar-angular";
import { HasPermissionDirective } from "../../shared/directives/has-permission/has-permission.directive";
import { PermissionService } from "../../services/api/permission/permission.service";
import { SvgIconComponent } from "../../shared/components/svg-icon/svg-icon.component";
import { ConfirmationService, MessageService } from "@openng/optimus-ui/api";
import { Update, UPDATE_STATUS } from "../../models/api/deviceManagement/update";
import { DatePipe, NgClass } from "@angular/common";
import { DatePickerModule } from "@openng/optimus-ui/datepicker";
import { DateTime } from "luxon";
import { TenantAdministrationService } from "../../services/api/tenant-administration/tenant-administration.service";
import { toSignal } from "@angular/core/rxjs-interop";
import { MultiSelectModule } from "@openng/optimus-ui/multiselect";
import { DeviceImport } from "./components/device-import/device-import";

@Component({
  standalone: true,
  selector: "app-device-management",
  imports: [
    TableModule,
    ProgressSpinnerModule,
    InputTextModule,
    CheckboxModule,
    FormsModule,
    ButtonComponent,
    TranslateModule,
    SelectModule,
    TabsModule,
    SimplebarAngularModule,
    HasPermissionDirective,
    SvgIconComponent,
    DatePipe,
    DatePickerModule,
    NgClass,
    MultiSelectModule,
  ],
  templateUrl: "./device-management.component.html",
  styleUrl: "./device-management.component.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [DatePipe],
})
export class DeviceManagementComponent implements OnInit {
  public translate = inject(TranslateService);
  public permissions = inject(PermissionService);

  private deviceManagement = inject(DeviceManagementService);
  private tenantService = inject(TenantAdministrationService);
  private confirmation = inject(ConfirmationService);
  private dialog = inject(DialogService);
  private message = inject(MessageService);

  public deviceStatusOptions = DEVICE_STATUS;

  private softwareRef!: DynamicDialogRef | null;

  // Devices Variables:
  public devicesCols: Column[] = [
    { field: "coreId", header: "device_management.devices.table.core_id" },
    {
      field: "deviceType",
      header: "device_management.devices.table.device_type",
    },
    { field: "tenant", header: "device_management.devices.table.tenant" },
    {
      field: "softwareVersion",
      header: "device_management.devices.table.software_version",
    },
    {
      field: "hardwareVersion",
      header: "device_management.devices.table.hardware_version",
    },
  ];
  public deviceTypes = signal([{ type: DEVICE_TYPE.CORE }, { type: DEVICE_TYPE.GUARD }, { type: DEVICE_TYPE.BUTLER }]);
  public devices: WritableSignal<DeviceTableRow[] | null> = signal(null);

  public tenants = toSignal(
    this.tenantService
      .getTenants()
      .pipe(map((tenants) => tenants.filter((t) => t.cores?.length ?? 0 > 0).map((t) => t.label)))
  );
  public softwareVersionOptions = signal<string[]>([]);
  public loadingDevices = computed(() => this.devices() === null);

  private startUpdateRef: DynamicDialogRef | null = null;
  private startImportRef: DynamicDialogRef | null = null;

  // Software Versions Variables:
  public allDeviceTypes = DEVICE_TYPE;
  public deviceTypesTabs = signal([
    { label: DEVICE_TYPE.CORE, value: DEVICE_TYPE.CORE },
    { label: DEVICE_TYPE.GUARD, value: DEVICE_TYPE.GUARD },
    { label: DEVICE_TYPE.BUTLER, value: DEVICE_TYPE.BUTLER },
  ]);
  private selectedTab: DeviceType = DEVICE_TYPE.CORE;
  public displayedSoftwareVersions: WritableSignal<Software[]> = signal([]);

  // Updates Variables:
  public deviceUpdatesCols: Column[] = [
    {
      field: "startedAt",
      header: "device_management.updates.table.started_at",
    },
    {
      field: "softwareVersion",
      header: "device_management.updates.table.software_version",
    },
    {
      field: "deviceType",
      header: "device_management.updates.table.device_type",
    },
  ];
  public deviceUpdates: WritableSignal<Update[]> = signal([]);
  public startDate = new Date();
  public endDate = new Date();
  public updateStatus = UPDATE_STATUS;

  constructor() {
    this.startDate = DateTime.fromJSDate(this.startDate).minus({ months: 1 }).toJSDate();
  }
  ngOnInit(): void {
    this.getSoftwares(DEVICE_TYPE.CORE);
    this.getDevicesUpdates(this.startDate.getTime());
    this.getDevices();
  }

  /*
   * DEVICES FUNCTIONS:
   */

  /**
   * Gets a list of devices from the server and updates the local state.
   * Also extracts all unique software versions from the devices and updates the local state.
   */
  private getDevices() {
    this.deviceManagement
      .getDevices()
      .pipe(
        tap((devices) => {
          const distinctSoftwareVersions = [
            ...new Set(devices.map((device) => device.softwareVersion).filter((v) => !!v)),
          ];
          this.softwareVersionOptions.set(distinctSoftwareVersions);
        })
      )
      .subscribe({
        next: (devices) => {
          this.devices.set(devices);
        },
      });
  }

  /**
   * Toggles the selected state of all devices in the table, or all filtered devices if the table has a filtered value.
   * @param checkValue - The value to set the selected state to.
   * @param table - The table containing the devices to toggle the selected state for.
   * @returns The updated array of devices.
   */
  public toggleSelectAllVisible(checkValue: boolean, table: Table) {
    const filteredDeviceIds: string[] = table.filteredValue?.map((device) => device.deviceId as string) || [];
    this.devices.update((devices) => {
      devices?.forEach((device) => {
        if (!table.filteredValue) {
          device.selected = checkValue;
        } else if (device.deviceId && filteredDeviceIds.includes(device.deviceId)) {
          device.selected = checkValue;
        }
      });
      return devices;
    });
  }

  /**
   * Applies a global filter to the specified table.
   * The filter uses the "contains" match mode to filter table data based on user input.
   * @param dt The table to apply the filter to.
   * @param event The input event containing the target HTMLInputElement.
   */
  public filterGlobal(dt: Table, event: Event) {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    dt.filterGlobal(value, "contains");
  }

  /**
   * Checks if all selected devices are of the same device type.
   * @return {boolean} True if all selected devices are of the same device type, false otherwise.
   */
  public checkSoftwareUpdate(): boolean {
    const selectedDevices = this.devices()?.filter((device) => device.selected);
    if (selectedDevices?.length === 0) return false;
    const firstSelectedDevice = selectedDevices?.[0];
    const isSameDeviceType = selectedDevices?.every((device) => device.deviceType === firstSelectedDevice?.deviceType);
    return !!isSameDeviceType;
  }

  /**
   * Opens a dialog for updating the software of selected devices.
   * The dialog displays a list of selected devices and their corresponding software versions.
   * If the user confirms the update, the dialog will close with a success flag,
   * triggering the component to refresh the devices and devices updates lists.
   */
  public openUpdateDialog(): void {
    if (this.checkSoftwareUpdate()) {
      const selectedDevices = this.devices()?.filter((device) => device.selected);
      this.startUpdateRef = this.dialog.open(StartUpdateComponent, {
        header: this.translate.instant("device_management.devices.update_dialog.header"),
        modal: true,
        width: "50vw",
        contentStyle: { overflow: "auto" },
        breakpoints: {
          "960px": "75vw",
          "640px": "90vw",
        },
        data: {
          devices: selectedDevices,
          deviceType: selectedDevices?.[0]?.deviceType,
        },
        closable: true,
      });
      this.startUpdateRef?.onClose.subscribe({
        next: (success: boolean) => {
          if (success) {
            this.getDevices();
            this.getDevicesUpdates(this.startDate.getTime());
          }
        },
      });
    }
  }

  /*
   * SOFTWARE VERSIONS FUNCTIONS:
   */

  /**
   * Retrieves a list of software versions from the server and updates the local state.
   * @param {DeviceType} deviceType - The type of device to get software versions for.
   * @param {boolean} [refresh=false] - Whether to refresh the software version list by re-fetching from the server.
   */
  private getSoftwares(deviceType: DeviceType, refresh = false) {
    this.deviceManagement
      .getSoftwares(deviceType, refresh)
      .pipe(first())
      .subscribe((softwares) => {
        this.displayedSoftwareVersions.set(softwares);
      });
  }

  /**
   * Changes the currently selected device type tab and updates the software version list accordingly.
   * @param event The event containing the device type to switch to.
   */
  public changeTab(event: string | number | undefined) {
    if (event) {
      const deviceType = event as DeviceType;
      this.selectedTab = deviceType;
      this.getSoftwares(deviceType);
    }
  }

  /**
   * Opens a dialog for adding or editing a software version.
   * The dialog displays a form for specifying the software version details.
   * If the user confirms the addition or edit, the dialog will close with a success flag,
   * triggering the component to refresh the software version list.
   * @param {Software | null} software - The software version to edit, or null to add a new one.
   */
  public openSoftwareDialog(software: Software | null): void {
    this.softwareRef = this.dialog.open(SoftwareForm, {
      header: this.translate.instant("device_management.software.add_software"),
      modal: true,
      width: "50vw",
      contentStyle: { overflow: "auto" },
      breakpoints: {
        "960px": "75vw",
        "640px": "90vw",
      },
      data: {
        software,
      },
      closable: true,
    });
    this.softwareRef?.onClose.subscribe({
      next: (success: boolean) => {
        if (success) {
          this.getSoftwares(this.selectedTab, true);
        }
      },
    });
  }

  /**
   * Deletes a software version.
   * Opens a confirmation dialog when called.
   * If the user confirms the deletion, the dialog will close with a success flag,
   * triggering the component to refresh the software version list.
   * @param event The event containing the target element.
   * @param software The software version to delete.
   */
  public deleteSoftware(event: Event, software: Software): void {
    this.confirmation.confirm({
      target: event.currentTarget as EventTarget,
      message: this.translate.instant("device_management.software.delete"),
      key: "global",
      accept: () => {
        this.deviceManagement
          .deleteSoftware(software.versionId)
          .pipe(first())
          .subscribe(() => {
            this.getSoftwares(this.selectedTab, true);
          });
      },
    });
  }

  /*
   * UPDATES FUNCTIONS:
   */

  /**
   * Gets a list of updates since or in between two timestamps from the server.
   * The list is updated in the local state.
   * @param {number} startTs - The start time of the update period in milliseconds.
   * @param {number} [endTs] - The end time of the update period in milliseconds. If not provided, server returns all updates since startTs.
   */
  private getDevicesUpdates(startTs: number, endTs?: number) {
    const startTsInSeconds = Math.round(startTs / 1000);
    let endTsInSeconds;
    if (endTs) {
      endTsInSeconds = Math.round(endTs / 1000);
    }
    this.deviceManagement
      .getDevicesUpdates(startTsInSeconds, endTsInSeconds)
      .pipe(first())
      .subscribe((updates) => {
        this.deviceUpdates.set(updates);
      });
  }

  /**
   * Stops a running update process.
   * The component will refresh the devices and devices updates lists after stopping the update.
   * A success message will be displayed if the update is stopped successfully.
   * @param {Update} update - The update to stop.
   */
  public stopUpdate(update: Update) {
    this.deviceManagement.stopUpdate(update.updateId).subscribe({
      next: () => {
        this.getDevices();
        this.getDevicesUpdates(this.startDate.getTime(), this.endDate.getTime());
        this.message.add({
          key: "global",
          severity: "success",
          summary: this.translate.instant("common.success"),
          detail: this.translate.instant("device_management.updates.stop_success"),
        });
      },
    });
  }

  /**
   * Downloads the update protocol log of a given update as a text file.
   * The file name will be in the format <software_version>_<date>_<time>.txt, where <date> is in the format dd-MM-yyyy and <time> is in the format HH:mm.
   * A success message will be displayed if the update protocol log is downloaded successfully.
   * @param {Update} update - The update to download the protocol log for.
   */
  public getUpdateProtocol(update: Update) {
    this.deviceManagement.getUpdateProtocol(update.updateId).subscribe({
      next: (res) => {
        const dateString = DateTime.fromSeconds(update.statusAt).toFormat("dd-MM-yyyy_HH-mm");
        this.downloadTextFile(res, `${update.softwareVersion}_${dateString}.txt`);
        this.message.add({
          key: "global",
          severity: "success",
          summary: this.translate.instant("common.success"),
          detail: this.translate.instant("device_management.updates.protocol_success"),
        });
      },
    });
  }

  /**
   * Downloads a given text content as a text file with a given filename.
   * @param {string} content - The text content to be downloaded.
   * @param {string} filename - The filename of the downloaded file.
   */
  private downloadTextFile(content: string, filename: string): void {
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();

    window.URL.revokeObjectURL(url);
  }
  /**
   * Called when the user selects a date range from the date picker component.
   * Retrieves the list of updates for all devices within the selected date range.
   */
  public onDateSelect() {
    this.getDevicesUpdates(this.startDate.getTime(), this.endDate.getTime());
  }

  /**
   * Opens a dialog for starting a device import feature.
   * If the user closes the dialog the component will refresh the devices list.
   */
  public openStartImport() {
    this.startImportRef = this.dialog.open(DeviceImport, {
      header: this.translate.instant("device_management.import.start_import"),
      modal: true,
      width: "50vw",
      contentStyle: { overflow: "auto" },
      breakpoints: {
        "960px": "75vw",
        "640px": "90vw",
      },
      closable: true,
    });
    this.startImportRef?.onClose.subscribe({
      next: () => {
        this.getDevices();
      },
    });
  }
}
